import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBackCircleOutline, IoChevronDown } from 'react-icons/io5';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

// Two amenity preview images for the slider
const AMENITY_IMAGES = [
  "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Fresidential1.jpg?alt=media&token=e4f1d0a5-1d30-4fcb-acc8-3798d173793b",
  "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Fresidential2.jpg?alt=media&token=9ad27eb3-10ae-40f3-917c-843d66ef4481"
];

// Caption for each slide
const SLIDE_CAPTIONS = [
  "Easy to Use",
  "Decorate Safely"
];

// Accordion data – partner revenue share benefits (service/referral path)
const whatItMeansItems = [
  {
    title: "A direct, recurring revenue stream",
    content:
      "You earn 20% of every tenant subscription, every month they stay. That's $9.00 per month for each Creator plan and $20.00 per month for each Pro plan, paid to you monthly for the life of their subscription."
  },
  {
    title: "Compounds quickly with zero overhead",
    content:
      "Just a handful of active subscribers can deliver hundreds to thousands in annual passive income. No support tickets, no management, no extra work on your end. Pure passive revenue that flows straight to your bottom line."
  },
  {
    title: "A premium perk tenants happily pay to keep",
    content:
      "Unlike a one-time move-in perk, this delivers ongoing value month after month. It keeps your tenants engaged and satisfied while generating reliable partner income you can count on."
  },
  {
    title: "Annual plans deepen your earnings",
    content:
      "Annual plans yield $90 per Creator and $200 per Pro each year. You can receive that as a lump sum or as 12 equal monthly payouts of $7.50 and $16.67 respectively, per the partner agreement."
  }
];

export default function ResidentialService() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [gridOpacity, setGridOpacity] = useState(0.3);
  const [openAccordion, setOpenAccordion] = useState([false, false, false, false]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});

  const lastGridChangeTime = useRef(0);
  const slideshowTimer = useRef(null);

  useEffect(() => {
    document.title = "Residential Service | Outlne";
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Grid animation
  useEffect(() => {
    if (!pageLoaded) return;
    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;
    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      const timeSinceChange = time - lastGridChangeTime.current;
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      animationFrame = requestAnimationFrame(animateScroll);
    };
    animationFrame = requestAnimationFrame(animateScroll);
    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid(prev => (prev + 1) % 3);
    }, 4000);
    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Auto‑slideshow for the amenity preview
  useEffect(() => {
    slideshowTimer.current = setInterval(() => {
      setActiveImageIndex(prev => (prev + 1) % AMENITY_IMAGES.length);
    }, 4000);
    return () => {
      if (slideshowTimer.current) clearInterval(slideshowTimer.current);
    };
  }, []);

  // Manual dot click resets the timer
  const handleDotClick = (index) => {
    setActiveImageIndex(index);
    if (slideshowTimer.current) clearInterval(slideshowTimer.current);
    slideshowTimer.current = setInterval(() => {
      setActiveImageIndex(prev => (prev + 1) % AMENITY_IMAGES.length);
    }, 4000);
  };

  const gridStyles = [
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`, backgroundSize: '40px 40px', display: activeGrid === 0 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`, backgroundSize: '50px 40px', backgroundPositionX: '20px', display: activeGrid === 1 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`, backgroundSize: '40px 30px', backgroundPositionY: '10px', display: activeGrid === 2 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
  ];

  const toggleAccordion = (index) => {
    setOpenAccordion(prev => prev.map((v, i) => i === index ? !v : v));
  };

  const handleProceed = () => {
    sessionStorage.setItem('selectedPlan', JSON.stringify({
      name: 'Service Inquiry',
      planType: 'referral',
      billingCycle: 'inquiry'
    }));
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    window.open(
      '/auth?audience=residential',
      'Outlne Sign Up',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  // Responsive typography system - consistent sizing across all breakpoints
  const typography = {
    // Main title
    mainTitle: {
      fontSize: isMobile ? '1.75rem' : isTablet ? '2.5rem' : '3.5rem',
      lineHeight: '1.1',
    },
    // Section headings
    sectionHeading: {
      fontSize: isMobile ? '1.75rem' : isTablet ? '2rem' : '2rem',
      lineHeight: '1.2',
    },
    // CTA heading
    ctaHeading: {
      fontSize: isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem',
      lineHeight: '1.2',
    },
    // Intro text paragraphs
    introText: {
      fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
      lineHeight: isMobile ? '1.6' : '1.6',
    },
    // Body text paragraphs
    bodyText: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1.05rem' : '1.125rem',
      lineHeight: isMobile ? '1.65' : '1.7',
    },
    // Centered emphasis text
    emphasisText: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1rem' : '1rem',
      lineHeight: '1.7',
    },
    // How it works description
    howItWorksDesc: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1.05rem' : '1.1rem',
      lineHeight: '1.6',
    },
    // Accordion title
    accordionTitle: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1rem' : '1.1rem',
    },
    // Accordion content
    accordionContent: {
      fontSize: isMobile ? '0.85rem' : isTablet ? '0.9rem' : '0.95rem',
      lineHeight: '1.7',
    },
    // Note text
    noteText: {
      fontSize: isMobile ? '0.8rem' : isTablet ? '0.825rem' : '0.85rem',
      lineHeight: '1.6',
    },
  };

  const centeredBlockStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    paddingLeft: isMobile || isTablet ? '15px' : '0',
    paddingRight: isMobile || isTablet ? '15px' : '0'
  };

  const accordionContainerStyle = {
    ...centeredBlockStyle,
    marginTop: '2rem',
    marginBottom: '2rem'
  };

  return (
    <>
      {/* Black background */}
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(29, 29, 29, 0.8)', zIndex: -3 }} />

      {/* Grid overlay */}
      <div style={{
        position: 'fixed', top: 0, left: 0, width: isMobile ? '100vw' : '50vw', bottom: 0,
        zIndex: -1, overflow: 'hidden', opacity: gridOpacity, transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute', inset: 0, width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, idx) => (
            <div key={idx} style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', ...style }} />
          ))}
        </div>
      </div>

      {/* Back button – desktop/tablet */}
      {!isMobile && (
        <div
          onClick={() => navigate('/residential')}
          style={{
            position: 'fixed', top: '30px', left: '30px',
            zIndex: 30, cursor: 'pointer',
            color: 'white', fontSize: '2rem',
            transition: 'opacity 0.2s ease',
          }}
          onMouseOver={e => e.currentTarget.style.opacity = 0.7}
          onMouseOut={e => e.currentTarget.style.opacity = 1}
          title="Back to Residential"
        >
          <IoArrowBackCircleOutline />
        </div>
      )}

      {/* Header */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: '40px', right: '40px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: 'auto' }} />
            <h1 style={{ fontSize: '1.8rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
          </div>
          <p style={{ fontSize: '1rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
            Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 8px', borderRadius: '4px', display: 'inline-block', minWidth: '60px', textAlign: 'center' }}>Editor</span>
          </p>
        </div>
      )}
      {isMobile && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '110px', backgroundColor: 'rgba(31, 31, 31, 0.8)', backdropFilter: 'blur(10px)', zIndex: 15 }} />
          <div style={{ position: 'fixed', top: '20px', left: '20px', zIndex: 20, textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
            <p style={{ fontSize: '0.9rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', minWidth: '50px', textAlign: 'center' }}>Editor</span>
            </p>
          </div>
          <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: '40px' }} />
            <div
              onClick={() => navigate('/residential')}
              style={{ cursor: 'pointer', marginRight: '5px', fontSize: '1.8rem', color: 'white', marginTop: '2px' }}
            >
              <IoArrowBackCircleOutline />
            </div>
          </div>
        </>
      )}

      {/* Main content */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5,
        overflowY: 'auto', overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0, transition: 'opacity 1s ease',
        color: 'white', fontFamily: 'avenir-lt-pro, sans-serif',
      }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto',
          padding: isMobile ? '120px 24px 40px' : isTablet ? '140px 32px 60px' : '160px 40px 80px'
        }}>

          {/* Title */}
          <h1 style={{
            fontFamily: 'didot, sans-serif',
            fontStyle: 'italic',
            fontSize: typography.mainTitle.fontSize,
            fontWeight: 400, 
            marginBottom: '2rem',
            lineHeight: typography.mainTitle.lineHeight, 
            letterSpacing: '-0.5px', 
            textAlign: 'center',
            paddingLeft: isMobile || isTablet ? '15px' : '0',
            paddingRight: isMobile || isTablet ? '15px' : '0',
          }}>
            Outlne as a Service<br />for your tenants
          </h1>

          {/* Intro text with image carousel */}
          <div style={{
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: '40px', 
            alignItems: 'center',
            marginBottom: '4rem',
            textAlign: 'center',
            paddingLeft: isMobile || isTablet ? '15px' : '0',
            paddingRight: isMobile || isTablet ? '15px' : '0',
          }}>
            <div style={{ flex: 1 }}>
              <p style={{
                fontSize: typography.introText.fontSize, 
                lineHeight: typography.introText.lineHeight, 
                color: '#ccc',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                Every tenant signs a lease with the same feeling: this could be home. A blank space, full of potential, waiting for their touch.
              </p>
              <p style={{
                fontSize: typography.introText.fontSize, 
                lineHeight: typography.introText.lineHeight, 
                color: '#ccc',
                marginTop: '1rem',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                But getting from that first vision to an approved renovation isn't instant. Tenants need a responsible way to picture the changes, stay within your building's guidelines, and come to you with an approval-ready plan. That takes the right tools in their hands, which is exactly what we’ve built.
              </p>
            </div>

            {/* Image carousel with auto‑slideshow and two dots */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? '100%' : 'auto'
            }}>
              <div style={{
                border: '3px solid white',
                borderRadius: '28px',
                overflow: 'hidden',
                width: isMobile ? 'auto' : '100%',
                maxWidth: isMobile ? 'none' : '280px',
                height: isMobile ? '450px' : 'auto',
                aspectRatio: '9 / 16',
                position: 'relative',
                backgroundColor: '#1a1a1a'
              }}>
                {/* Shimmer skeleton placeholder */}
                {AMENITY_IMAGES.map((_, idx) => (
                  !imagesLoaded[idx] && (
                    <div
                      key={`shimmer-${idx}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite linear',
                        zIndex: 1,
                        transition: 'opacity 0.4s ease',
                        opacity: idx === activeImageIndex ? 1 : 0,
                      }}
                    />
                  )
                ))}
                {AMENITY_IMAGES.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`Service preview ${idx + 1}`}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setImagesLoaded(prev => ({ ...prev, [idx]: true }))}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'opacity 0.6s ease',
                      opacity: idx === activeImageIndex ? 1 : 0,
                    }}
                  />
                ))}
                <div style={{
                  position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(203, 203, 203, 0.25)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                   color: 'white', padding: '6px 20px', borderRadius: '20px',
                  fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap'
                }}>
                  {SLIDE_CAPTIONS[activeImageIndex]}
                </div>
              </div>

              {/* Circle navigation dots */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '16px',
                justifyContent: 'center',
              }}>
                {AMENITY_IMAGES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDotClick(idx)}
                    aria-label={`Image ${idx + 1}`}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: idx === activeImageIndex ? '#408aff' : '#555',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Additional intro – centered */}
          <div style={centeredBlockStyle}>
            
            <p style={{
              fontSize: typography.bodyText.fontSize,
              lineHeight: typography.bodyText.lineHeight,
              marginBottom: '1.5rem', 
              color: '#ccc',
              textAlign: isMobile ? 'center' : 'left'
            }}>
             Offering Outlne as a service gives tenants the ability to request approval and decorate safely: preview furniture, try different paint colors and creative apartment-friendly layouts around their space, before any physical changes are made.
            </p>
            <p style={{
              fontSize: typography.bodyText.fontSize,
              lineHeight: typography.bodyText.lineHeight,
              marginBottom: '1.5rem', 
              color: '#ccc',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              So when the conversation about making rental changes happens, they're coming to you with a clear visual plan, not a vague, risky idea. That makes the process safer and easier for everyone, while naturally increasing retention, because tenants who've invested time in personalizing their home feel far more attached and are likely to stay long term.
            </p>
            <p style={{
              fontSize: typography.emphasisText.fontSize,
              lineHeight: typography.emphasisText.lineHeight,
              marginBottom: '2rem', 
              color: '#ccc',
              textAlign: 'center', 
              fontWeight: 500
            }}>
              Apartment-friendly planning for your units, made safe and easy.
            </p>
          </div>

          {/* White separator */}
          <hr style={{
            border: 'none', borderTop: '1px solid rgba(255,255,255,0.25)',
            margin: '3rem auto', maxWidth: '300px',
          }} />

          {/* How the Subscription works – See Pricing button */}
          <section style={{ margin: '4rem 0', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: typography.sectionHeading.fontSize, 
              fontWeight: 400, 
              marginBottom: '1.5rem' 
            }}>
              How the Subscription Works
            </h2>
            <p style={{ 
              color: '#ccc', 
              marginBottom: '2rem', 
              fontSize: typography.howItWorksDesc.fontSize,
              lineHeight: typography.howItWorksDesc.lineHeight 
            }}>
              Tenants choose a plan that fits their needs. They'll receive a special referral code to ensure you get your partner percentage.
            </p>
            <button
              onClick={() => navigate('/pricing?ref=residential-service')}
              style={{
                backgroundColor: '#40a3ff', border: 'none', color: 'white',
                fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.2rem', fontWeight: 500,
                padding: '14px 40px', borderRadius: '40px', cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#2f8ad6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#40a3ff'}
            >
              See Pricing
            </button>
          </section>

          {/* What This Means for You – Accordion */}
          <section style={{ margin: '4rem 0' }}>
            <h2 style={{ 
              fontFamily: 'didot, sans-serif',
              fontStyle: 'italic',
              fontSize: typography.sectionHeading.fontSize, 
              fontWeight: 400, 
              textAlign: 'center', 
              marginBottom: '2rem' 
            }}>
              What This Means for You
            </h2>
            <div style={accordionContainerStyle}>
              {whatItMeansItems.map((item, index) => (
                <div key={index} style={{ marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                  <div
                    onClick={() => toggleAccordion(index)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0',
                      cursor: 'pointer',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: typography.accordionTitle.fontSize,
                    }}
                  >
                    <span>{item.title}</span>
                    <IoChevronDown
                      style={{
                        color: '#40a3ff',
                        transition: 'transform 0.3s ease',
                        transform: openAccordion[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                        fontSize: '1.4rem',
                      }}
                    />
                  </div>
                  <div style={{
                    maxHeight: openAccordion[index] ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                  }}>
                    <p style={{
                      color: '#ccc',
                      lineHeight: typography.accordionContent.lineHeight,
                      padding: '0 0 1rem 0',
                      margin: 0,
                      fontSize: typography.accordionContent.fontSize,
                    }}>
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}
              <p style={{ 
                color: '#aaa', 
                fontSize: typography.noteText.fontSize, 
                textAlign: 'center', 
                marginTop: '2rem', 
                lineHeight: typography.noteText.lineHeight 
              }}>
                Revenue numbers reflect the standard monthly subscription share. Annual plans yield $90/year per Creator and $200/year per Pro, paid as 12 equal monthly payouts of $7.50 and $16.67 respectively, per the partner agreement.
              </p>
            </div>
          </section>

          {/* Ready to Offer */}
          <section style={{ textAlign: 'center', margin: '5rem 0 3rem' }}>
            <h2 style={{ 
              fontFamily: 'didot, sans-serif',
              fontStyle: 'italic',
              fontSize: typography.ctaHeading.fontSize, 
              fontWeight: 400, 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              Ready to Offer This to Your Tenants?
            </h2>
            <button
              onClick={handleProceed}
              style={{
                backgroundColor: '#40a3ff', border: 'none', color: 'white',
                fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.2rem', fontWeight: 500,
                padding: '14px 40px', borderRadius: '40px', cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#2f8ad6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#40a3ff'}
            >Enroll</button>
          </section>
        </div>

        <Footer />
      </div>

      <style>{`
        html, body, #root {
          overflow: hidden; height: 100%; width: 100%; position: fixed;
          margin: 0; padding: 0;
        }
        * {
          -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
          -webkit-tap-highlight-color: transparent;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </>
  );
}